<template>
  <div class="p-4">
    <!-- Кнопка "Добавить рабочий центр" -->
    <button
      v-can-write="'workshop_post_types'"
      @click="openAddWorkshopPostModal"
      class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
    >
      Добавить рабочий центр
    </button>

    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица рабочих центров -->
    <table v-else class="w-full border-collapse bg-white shadow-md">
      <thead class="bg-gray-100">
        <tr>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">#</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Название рабочего центра</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Цех</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Действия</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(workshopPost, index) in workshopPosts" :key="workshopPost.id" class="border-b hover:bg-gray-50">
          <td class="px-4 py-2 text-sm text-gray-700">{{ index + 1 }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ workshopPost.name }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ getWorkshopName(workshopPost.workshop_id) || 'Не указан' }}</td>
          <td class="px-4 py-2">
            <button
              v-can-write="'workshop_post_types'"
              @click="confirmDelete(workshopPost.id)"
              class="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Удалить
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Модальное окно для добавления/редактирования рабочего центра -->
    <div v-if="addWorkshopPostModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">{{ isEditMode ? 'Редактировать рабочий центр' : 'Добавить рабочий центр' }}</h2>
        <form @submit.prevent="saveWorkshopPost">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Название рабочего центра</label>
            <input
              type="text"
              v-model="newWorkshopPost.name"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите название рабочего центра"
              required
            />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Цех</label>
            <select
              v-model="newWorkshopPost.workshop_id"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Выберите цех</option>
              <option v-for="workshop in workshops" :key="workshop.id" :value="workshop.id">
                {{ workshop.name }}
              </option>
            </select>
          </div>
          <div class="flex justify-end space-x-2">
            <button
              type="button"
              @click="closeAddWorkshopPostModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Отмена
            </button>
            <button
              type="submit"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {{ isEditMode ? 'Сохранить' : 'Добавить' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="isConfirmModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Подтвердите удаление</h2>
        <p class="text-sm text-gray-700 mb-4">Вы уверены, что хотите удалить этот рабочий центр?</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            @click="proceedDelete"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      workshopPosts: [], // Список рабочих центров
      workshops: [], // Список цехов
      loading: true, // Флаг загрузки
      addWorkshopPostModalVisible: false, // Флаг видимости модального окна добавления/редактирования
      isEditMode: false, // Флаг режима редактирования
      newWorkshopPost: {
        id: null, // ID редактируемого рабочего центра (если есть)
        name: '', // Название рабочего центра
        workshop_id: '', // ID цеха
      },
      isConfirmModalVisible: false, // Флаг видимости модального окна подтверждения
      workshopPostIdToDelete: null, // ID рабочего центра для удаления
    };
  },
  created() {
    this.getWorkshops();
    this.getWorkshopPosts();
  },
  methods: {
    /**
     * Загружает список цехов.
     */
    getWorkshops() {
      axios
        .post('/api/workshop/getWorkshops')
        .then((response) => {
          this.workshops = response.data.data;
        })
        .catch((error) => {
          console.error('Ошибка при загрузке цехов:', error);
          alert('Не удалось загрузить цеха. Попробуйте позже.');
        });
    },

    /**
     * Загружает список рабочих центров.
     */
    getWorkshopPosts() {
      axios
        .post('/api/workshopPost/getWorkshopPosts', { module_id: this.$route.params.id })
        .then((response) => {
          this.workshopPosts = response.data.data;
          this.loading = false;
        })
        .catch((error) => {
          console.error('Ошибка при загрузке рабочих центров:', error);
          alert('Не удалось загрузить рабочие центры. Попробуйте позже.');
          this.loading = false;
        });
    },

    /**
     * Возвращает название цеха по его ID.
     */
    getWorkshopName(workshopId) {
      const workshop = this.workshops.find((w) => w.id === workshopId);
      return workshop ? workshop.name : null;
    },

    /**
     * Показывает модальное окно добавления рабочего центра.
     */
    openAddWorkshopPostModal() {
      this.isEditMode = false; // Режим создания
      this.newWorkshopPost = { id: null, name: '', workshop_id: '' }; // Очищаем данные
      this.addWorkshopPostModalVisible = true;
    },

    /**
     * Показывает модальное окно редактирования рабочего центра.
     */
    openEditWorkshopPostModal(workshopPost) {
      this.isEditMode = true; // Режим редактирования
      this.newWorkshopPost = { ...workshopPost }; // Копируем данные рабочего центра
      this.addWorkshopPostModalVisible = true;
    },

    /**
     * Сохраняет или обновляет рабочий центр.
     */
    saveWorkshopPost() {
      if (!this.newWorkshopPost.name || !this.newWorkshopPost.workshop_id) {
        alert('Заполните все поля');
        return;
      }

      const url = this.isEditMode
        ? '/api/workshopPost/createOrUpdateWorkshopPost'
        : '/api/workshopPost/createOrUpdateWorkshopPost';

      axios
        .post(url, this.newWorkshopPost)
        .then(() => {
          this.getWorkshopPosts(); // Обновляем список рабочих центров
          this.closeAddWorkshopPostModal(); // Закрываем модальное окно
        })
        .catch((error) => {
          console.error('Ошибка при сохранении рабочего центра:', error);
          alert('Не удалось сохранить рабочий центр. Попробуйте позже.');
        });
    },

    /**
     * Закрывает модальное окно добавления/редактирования.
     */
    closeAddWorkshopPostModal() {
      this.addWorkshopPostModalVisible = false;
      this.newWorkshopPost = { id: null, name: '', workshop_id: '' }; // Очищаем данные
    },

    /**
     * Подтверждает удаление рабочего центра.
     */
    confirmDelete(id) {
      this.workshopPostIdToDelete = id;
      this.isConfirmModalVisible = true;
    },

    /**
     * Отменяет удаление рабочего центра.
     */
    cancelDelete() {
      this.isConfirmModalVisible = false;
      this.workshopPostIdToDelete = null;
    },

    /**
     * Удаляет рабочий центр.
     */
    proceedDelete() {
      if (!this.workshopPostIdToDelete) {
        console.error('ID рабочего центра не указан');
        return;
      }

      axios
        .post('/api/workshopPost/deleteWorkshopPost', { id: this.workshopPostIdToDelete })
        .then(() => {
          this.getWorkshopPosts(); // Обновляем список рабочих центров
          this.cancelDelete(); // Закрываем модальное окно
        })
        .catch((error) => {
          console.error('Ошибка при удалении рабочего центра:', error);
          alert('Не удалось удалить рабочий центр. Попробуйте позже.');
        });
    },
  },
};
</script>
